import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Truck, Zap, Droplet, Calendar, Award, MapPin, ArrowRight, Check, Sparkles, Phone } from 'lucide-react'

// USPs
const usps = [
  { icon: Calendar, title: '365 Tage verfügbar', desc: 'Ganzjährig für Ihr Event buchbar' },
  { icon: MapPin, title: 'Krefeld & Umgebung', desc: 'Flexibel an Ihrem Wunschort' },
  { icon: Sparkles, title: 'Frisch zubereitet', desc: 'Hochwertige Zutaten, vor Ort gekocht' },
  { icon: Truck, title: 'Komplett-Service', desc: 'Von Aufbau bis Reinigung' }
]

// Benefits
const benefits = [
  'Professioneller Foodtruck im modernen Design',
  'Erfahrenes Koch-Team vor Ort',
  'Frische Zubereitung während Ihres Events',
  'Individuelle Menü-Zusammenstellung',
  'Kompletter Auf- und Abbau',
  'Professionelles Equipment & Geschirr',
  'Flexible Zeitfenster',
  'Hygiene-Standards nach HACCP'
]

export default function Foodtruck() {
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll()
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0])
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 1.1])

  return (
    <div className="bg-[#0a0a0a]">
      
      {/* ════════════════════════════════════════════════════════════
          HERO SECTION
      ════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative h-[50vh] min-h-[400px] max-h-[550px] w-full overflow-hidden">
        <motion.div style={{ scale: heroScale }} className="absolute inset-0">
          <img
            src="/foodtruck.jpg"
            alt="TILLZ Landküche Foodtruck"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.35)' }}
          />
        </motion.div>

        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-[#0a0a0a]" />
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-brand-800/10 rounded-full blur-[120px]" />
        
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 pt-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-4"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full">
              <Truck className="w-3 h-3 text-brand-400" />
              <span className="text-white/70 text-xs uppercase tracking-[0.25em]">TILLZ Landküche</span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light tracking-tight leading-[1.1] mb-4"
          >
            Kulinarik <span className="italic text-white/70">auf Rädern</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="text-white/50 text-sm md:text-base max-w-xl mb-6 leading-relaxed"
          >
            365 Tage im Jahr verfügbar für Veranstaltungen in und um Krefeld.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="flex flex-row gap-3"
          >
            <Link to="/kontakt">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-2.5 bg-white text-black text-xs uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500"
              >
                Foodtruck buchen
              </motion.button>
            </Link>
            <a href="tel:+4915755537615">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-2.5 border border-white/30 text-white text-xs uppercase tracking-[0.2em] font-bold rounded-full hover:bg-white/10 transition-all duration-500"
              >
                Telefon-Beratung
              </motion.button>
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          USPs
      ════════════════════════════════════════════════════════════ */}
      <section className="border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {usps.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="py-10 md:py-12 px-6 text-center border-r border-white/5 last:border-r-0 group hover:bg-white/[0.02] transition-colors flex flex-col items-center justify-center h-[140px]"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 mb-3 group-hover:scale-110 transition-transform">
                  <item.icon className="w-4 h-4 text-brand-400" />
                </div>
                <p className="text-white text-sm font-medium mb-1">{item.title}</p>
                <p className="text-white/40 text-xs">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          FOODTRUCK BILD
      ════════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative overflow-hidden rounded-2xl md:rounded-3xl border border-white/10 shadow-2xl"
          >
            <img
              src="/foodtruck.jpg"
              alt="TILLZ Landküche Foodtruck – Ihr mobiler Genuss für Events"
              className="w-full h-auto object-cover aspect-[16/10] md:aspect-[16/9]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          TECHNICAL REQUIREMENTS
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 md:py-32">
        <div className="absolute top-40 right-20 w-[500px] h-[500px] bg-brand-800/5 rounded-full blur-[120px]" />
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left - Title */}
            <div className="lg:sticky lg:top-32">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4">Anforderungen</p>
                <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light mb-6">
                  Technische <span className="italic text-white/60">Voraussetzungen</span>
                </h2>
                <div className="h-px w-20 bg-brand-800 mb-6" />
                <p className="text-white/50 leading-relaxed">
                  Damit wir Ihnen den besten Service bieten können, benötigen wir folgende Anschlüsse vor Ort.
                </p>
              </motion.div>
            </div>

            {/* Right - Requirements */}
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="group p-6 bg-white/[0.02] border border-white/5 rounded-2xl hover:border-brand-800/30 transition-all duration-500"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center flex-shrink-0">
                    <Droplet className="w-5 h-5 text-brand-400" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-white mb-2">Wasseranschluss</h3>
                    <p className="text-white/50 text-sm leading-relaxed">
                      Standard-Gartenschlauch-Anschluss ist ausreichend.
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="group p-6 bg-white/[0.02] border border-white/5 rounded-2xl hover:border-brand-800/30 transition-all duration-500"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-brand-400" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-white mb-2">Starkstromanschluss</h3>
                    <p className="text-white/50 text-sm leading-relaxed">
                      CEE 32A / 400V für den Betrieb der Küchengeräte.
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="p-4 bg-brand-800/5 border border-brand-800/10 rounded-xl"
              >
                <p className="text-white/50 text-sm">
                  <span className="text-brand-400 font-medium">Hinweis:</span> Nicht sicher, ob die Anschlüsse vorhanden sind? Wir beraten Sie gerne.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          BENEFITS
      ════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left - Title */}
            <div className="lg:sticky lg:top-32">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4">Leistungen</p>
                <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light mb-6">
                  Was Sie <span className="italic text-white/60">bekommen</span>
                </h2>
                <div className="h-px w-20 bg-brand-800 mb-6" />
                <p className="text-white/50 leading-relaxed">
                  Komplettservice von Aufbau bis Abbau – Sie müssen sich um nichts kümmern.
                </p>
              </motion.div>
            </div>

            {/* Right - List */}
            <div className="space-y-3">
              {benefits.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-start gap-4 p-4 border-l-2 border-white/10 hover:border-brand-800 hover:bg-white/[0.02] transition-all duration-300"
                >
                  <Check className="w-5 h-5 text-brand-400 flex-shrink-0" />
                  <p className="text-white">{item}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CTA SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="py-12 md:py-20 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-800/10 border border-brand-800/20 mb-8">
              <Truck className="w-7 h-7 text-brand-400" />
            </div>
            
            <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-6">Jetzt buchen</p>
            
            <h2 className="font-serif text-3xl md:text-5xl text-white font-light mb-8 leading-tight">
              Buchen Sie unseren<br />
              <span className="italic text-white/60">Foodtruck</span>
            </h2>
            
            <p className="text-white/40 text-lg mb-10 max-w-2xl mx-auto">
              Beliebte Event-Termine sind oft Monate im Voraus ausgebucht. Sichern Sie sich jetzt Ihren Wunschtermin.
            </p>

            <Link to="/kontakt">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-10 py-4 bg-white text-black text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500 flex items-center gap-3 mx-auto"
              >
                <Phone className="w-4 h-4" />
                Jetzt anfragen
              </motion.button>
            </Link>
            
            <p className="mt-8 text-white/30 text-xs uppercase tracking-[0.2em]">
              365 Tage verfügbar · Kostenlose Beratung · Angebot in 24h
            </p>
          </motion.div>
        </div>
      </section>

    </div>
  )
}
